import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface TrackingSettings {
  id: string;
  enabled: boolean;
  gtmContainerId: string;
  ga4MeasurementId: string;
  googleAdsId: string;
  metaPixelId: string;
  trackSpaPageViews: boolean;
  customHeadCode: string;
  customBodyCode: string;
  updatedAt?: Date;
}

export const defaultTrackingSettings: TrackingSettings = {
  id: 'tracking',
  enabled: true,
  gtmContainerId: '',
  ga4MeasurementId: '',
  googleAdsId: '',
  metaPixelId: '',
  trackSpaPageViews: true,
  customHeadCode: '',
  customBodyCode: ''
};

const trackingDocRef = () => doc(db, 'content', 'tracking');

/**
 * Чете настройките без да създава документ, ако липсва - публичният сайт няма
 * право да пише в Firestore. Документът се създава при първото запазване от
 * админ панела.
 */
export const getTrackingSettings = async (): Promise<TrackingSettings> => {
  const docSnap = await getDoc(trackingDocRef());
  if (!docSnap.exists()) return defaultTrackingSettings;

  const data = docSnap.data();
  return {
    ...defaultTrackingSettings,
    ...data,
    id: docSnap.id,
    updatedAt: data.updatedAt?.toDate()
  } as TrackingSettings;
};

export const useTrackingSettings = () => {
  const [settings, setSettings] = useState<TrackingSettings>(defaultTrackingSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setSettings(await getTrackingSettings());
      setError(null);
    } catch (err) {
      console.error('Error fetching tracking settings:', err);
      setError('Грешка при зареждане на настройките за проследяване');
      setSettings(defaultTrackingSettings);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings: Partial<TrackingSettings>) => {
    try {
      await setDoc(trackingDocRef(), {
        ...newSettings,
        updatedAt: serverTimestamp()
      });
      setSettings({ ...settings, ...newSettings, updatedAt: new Date() });
      return true;
    } catch (err) {
      console.error('Error saving tracking settings:', err);
      setError('Грешка при запазване на настройките за проследяване');
      return false;
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return {
    settings,
    loading,
    error,
    saveSettings,
    refreshSettings: fetchSettings
  };
};
